// Schema migration runner.
//
// The schema's "CREATE TABLE IF NOT EXISTS" baseline handles fresh
// databases. When a schema column changes shape — added, dropped,
// renamed, retyped — IF NOT EXISTS does nothing and the older
// rows stay incompatible. Migrations close that gap.
//
// Shape: an ordered list of `(from, to, migrator)` tuples. The
// runner reads `vfs_meta.schema_version` (defaulting to 0 when the
// row is absent), picks every migration whose `from === current`,
// runs it, advances `current`, and repeats until `current >=
// SCHEMA_VERSION`. The whole pass runs inside the caller's
// transactionSync so a partial migration rolls back.
//
// Each migrator is a `(db: Database) => void` and may assume the
// previous version's schema is in place. Migrators land schema
// changes only; they don't touch user data unless the column shape
// requires it.

import type { Database } from "../storage.js";

export interface Migration {
  readonly from: number;
  readonly to: number;
  readonly migrator: (db: Database) => void;
}

// v1 → v2 — add `_vfs_mounts.mode` so dofs can enforce read-only
// mounts at the data layer. Existing rows default to 'read-only';
// the workspace re-stamps them with the registered mount's mode on
// the next index pass.
//
// The CHECK constraint is duplicated in `sync.ts`'s fresh-install
// DDL; both paths must keep the same allowed set.
function v1_to_v2_add_mounts_mode(db: Database): void {
  db.run(
    `ALTER TABLE _vfs_mounts
       ADD COLUMN mode TEXT NOT NULL DEFAULT 'read-only'
       CHECK(mode IN ('read-only', 'read-write'))`,
  );
}

// v2 → v3 — denormalise file size onto vfs_nodes so stat doesn't
// have to SUM the chunk rows on every call. The column is
// backfilled from existing vfs_chunks; later writes maintain it.
function v2_to_v3_add_size_column(db: Database): void {
  const hasColumn = db
    .all<{ name: string }>("PRAGMA table_info(vfs_nodes)")
    .some((column) => column.name === "size");
  if (!hasColumn) {
    db.run("ALTER TABLE vfs_nodes ADD COLUMN size INTEGER NOT NULL DEFAULT 0");
  }
  db.run(
    `UPDATE vfs_nodes
        SET size = COALESCE(
          (SELECT SUM(size) FROM vfs_chunks WHERE vfs_chunks.inode = vfs_nodes.inode),
          0
        )
      WHERE type = 'file'`,
  );
}

// v3 → v4 — add a `backend` column to `_vfs_watermark` so a
// workspace can host more than one backend with independent sync
// cursors. SQLite's ALTER TABLE can't change a primary key; copy
// existing rows into a fresh table with the composite
// (k, backend) primary key, then swap the tables.
//
// Existing rows land under the `default` backend id, which the
// dofs sync helpers also use as the fallback when a caller
// doesn't pass an id. Pre-multi-backend workspaces keep their
// pushRev / fetchRev cursors intact through the upgrade.
function v3_to_v4_watermark_backend_column(db: Database): void {
  db.run(`ALTER TABLE _vfs_watermark RENAME TO _vfs_watermark_v3`);
  db.run(
    `CREATE TABLE _vfs_watermark (
       k       TEXT    NOT NULL,
       backend TEXT    NOT NULL DEFAULT 'default',
       v       INTEGER NOT NULL,
       PRIMARY KEY (k, backend)
     )`,
  );
  db.run(
    `INSERT INTO _vfs_watermark (k, backend, v)
       SELECT k, 'default', v FROM _vfs_watermark_v3`,
  );
  db.run(`DROP TABLE _vfs_watermark_v3`);
}

// v4 → v5 — rebuild `vfs_dirents` and `vfs_chunks` as WITHOUT ROWID.
// SQLite can't convert a table to WITHOUT ROWID in place, so for each
// table: rename it aside, create the WITHOUT ROWID replacement, copy
// the rows, drop the old table.
//
// Both targets are FK-inert (neither is an FK parent or child; the
// schema's only foreign key is vfs_blob_bytes -> vfs_blobs) and have
// composite primary keys with no AUTOINCREMENT, so WITHOUT ROWID is
// legal and sqlite_sequence is untouched. `vfs_blob_bytes` is left
// alone on purpose — it holds the large blob payloads and the FK.
//
// A RENAME carries the table's secondary index along to the temp
// name, and the following DROP takes the index with it. The baseline
// `CREATE INDEX IF NOT EXISTS` in initializeSchema already ran, before
// migrations, and does not re-run — so this migrator must recreate
// vfs_dirents_by_child and vfs_chunks_by_hash itself, or upgraded
// databases silently lose them. Keep the CREATE bodies in lockstep
// with the fresh-install DDL in core.ts.
function v4_to_v5_without_rowid(db: Database): void {
  // vfs_dirents
  db.run(`ALTER TABLE vfs_dirents RENAME TO vfs_dirents_v4`);
  db.run(
    `CREATE TABLE vfs_dirents (
       parent_inode INTEGER NOT NULL,
       name         TEXT    NOT NULL,
       child_inode  INTEGER NOT NULL,
       PRIMARY KEY (parent_inode, name)
     ) WITHOUT ROWID`,
  );
  db.run(
    `INSERT INTO vfs_dirents (parent_inode, name, child_inode)
       SELECT parent_inode, name, child_inode FROM vfs_dirents_v4`,
  );
  db.run(`DROP TABLE vfs_dirents_v4`);
  db.run(`CREATE INDEX vfs_dirents_by_child ON vfs_dirents(child_inode)`);

  // vfs_chunks
  db.run(`ALTER TABLE vfs_chunks RENAME TO vfs_chunks_v4`);
  db.run(
    `CREATE TABLE vfs_chunks (
       inode INTEGER NOT NULL,
       idx   INTEGER NOT NULL,
       hash  BLOB    NOT NULL,
       size  INTEGER NOT NULL,
       PRIMARY KEY (inode, idx)
     ) WITHOUT ROWID`,
  );
  db.run(
    `INSERT INTO vfs_chunks (inode, idx, hash, size)
       SELECT inode, idx, hash, size FROM vfs_chunks_v4`,
  );
  db.run(`DROP TABLE vfs_chunks_v4`);
  db.run(`CREATE INDEX vfs_chunks_by_hash ON vfs_chunks(hash)`);
}

function v5_to_v6_push_cursor(db: Database): void {
  db.run(
    `CREATE TABLE IF NOT EXISTS _vfs_push_cursor (
       k       TEXT    NOT NULL CHECK(k = 'push'),
       backend TEXT    NOT NULL DEFAULT 'default',
       rev     INTEGER NOT NULL DEFAULT 0,
       path    TEXT,
       PRIMARY KEY (k, backend)
     )`,
  );
  db.run(
    `INSERT OR IGNORE INTO _vfs_push_cursor (k, backend, rev, path)
       SELECT 'push', backend, v, NULL
       FROM _vfs_watermark
       WHERE k = 'pushRev'`,
  );
}

// v6 → v7 — add the restartable sync operation table and its skip
// log. Both are new tables, so the migration is a plain create; no
// existing rows need reshaping. Fresh installs land the same DDL from
// `sync.ts`, and both paths must keep the CHECK constraints aligned.
//
// Nothing is backfilled. An upgraded database has no in-flight
// operation by definition — the old code path had nowhere to record
// one — so the first pull or push after the upgrade captures a fresh
// target from the existing watermark cursor.
function v6_to_v7_sync_operations(db: Database): void {
  db.run(
    `CREATE TABLE IF NOT EXISTS _vfs_sync_operations (
       backend             TEXT    NOT NULL,
       direction           TEXT    NOT NULL CHECK (direction IN ('pull', 'push')),
       generation          TEXT    NOT NULL,
       status              TEXT    NOT NULL CHECK (
                             status IN ('capturing', 'pending', 'failed', 'lost')
                           ),
       target_rev          INTEGER,
       target_path         TEXT,
       runtime_id          TEXT,
       mode                TEXT    CHECK (mode IN ('entries', 'pack')),
       block_after_rev     INTEGER,
       block_after_path    TEXT,
       block_started_at    INTEGER,
       internal_max_entries INTEGER NOT NULL,
       internal_max_bytes  INTEGER NOT NULL,
       created_at          INTEGER NOT NULL,
       updated_at          INTEGER NOT NULL,
       last_error          TEXT,
       PRIMARY KEY (backend, direction)
     )`,
  );
  db.run(
    `CREATE TABLE IF NOT EXISTS _vfs_sync_skips (
       backend    TEXT    NOT NULL,
       direction  TEXT    NOT NULL CHECK (direction IN ('pull', 'push')),
       generation TEXT    NOT NULL,
       path       TEXT    NOT NULL,
       reason     TEXT    NOT NULL,
       at         INTEGER NOT NULL,
       PRIMARY KEY (backend, direction, generation, path)
     )`,
  );
}

// v7 → v8 — add `vfs_changes_by_op_rev`. The push tick's tombstone
// query filters `rev > ? AND op = 'delete'` and groups by path;
// without an (op, rev) index the planner scans vfs_changes in path
// order and never applies the rev predicate, reading the whole table
// to return the few rows inside the watermark window.
//
// Index-only migration: no table is rewritten and no row is touched,
// so existing tombstones carry through untouched. The CREATE is
// duplicated in `sync.ts`'s fresh-install DDL; keep the two in
// lockstep. IF NOT EXISTS keeps this safe if a database somehow
// already has the index.
function v7_to_v8_changes_op_rev_index(db: Database): void {
  db.run(`CREATE INDEX IF NOT EXISTS vfs_changes_by_op_rev ON vfs_changes(op, rev)`);
}

// v8 → v9 — record the fingerprint of the version each tombstone
// removed, so a receiver can apply a delete of exactly the version it
// holds even before the push cursor covers it. Additive and nullable:
// existing tombstones keep NULL, which receivers treat like a delete
// from a peer that sends no fingerprint. The column is duplicated in
// `sync.ts`'s fresh-install DDL.
function v8_to_v9_changes_prior(db: Database): void {
  const hasColumn = db
    .all<{ name: string }>("PRAGMA table_info(vfs_changes)")
    .some((column) => column.name === "prior");
  if (!hasColumn) {
    db.run("ALTER TABLE vfs_changes ADD COLUMN prior TEXT");
  }
}

// v9 → v10 — add `_vfs_mounts.version`, the content version a mount
// declared when it was last materialized. The workspace indexer
// compares it with the registered mount's version and re-materializes
// on a mismatch. Existing rows get NULL, which a versioned mount reads
// as a mismatch (one refresh), and an unversioned one ignores.
//
// Nullable and with no default, so the ALTER needs no backfill. The
// column check keeps the step safe on a database that already has it.
// The column is duplicated in `sync.ts`'s fresh-install DDL.
function v9_to_v10_add_mounts_version(db: Database): void {
  const hasColumn = db
    .all<{ name: string }>("PRAGMA table_info(_vfs_mounts)")
    .some((column) => column.name === "version");
  if (!hasColumn) {
    db.run("ALTER TABLE _vfs_mounts ADD COLUMN version TEXT");
  }
}

export const MIGRATIONS: readonly Migration[] = [
  { from: 1, to: 2, migrator: v1_to_v2_add_mounts_mode },
  { from: 2, to: 3, migrator: v2_to_v3_add_size_column },
  { from: 3, to: 4, migrator: v3_to_v4_watermark_backend_column },
  { from: 4, to: 5, migrator: v4_to_v5_without_rowid },
  { from: 5, to: 6, migrator: v5_to_v6_push_cursor },
  { from: 6, to: 7, migrator: v6_to_v7_sync_operations },
  { from: 7, to: 8, migrator: v7_to_v8_changes_op_rev_index },
  { from: 8, to: 9, migrator: v8_to_v9_changes_prior },
  { from: 9, to: 10, migrator: v9_to_v10_add_mounts_version },
] as const;

// Apply every migration whose `from` matches the current version,
// in order, until we reach the target. The caller has already
// wrapped this in a transactionSync; failures here roll the whole
// initializeSchema call back.
export function runMigrations(db: Database, current: number, target: number): number {
  let version = current;
  while (version < target) {
    const next = MIGRATIONS.find((m) => m.from === version);
    if (next === undefined) {
      // No migration registered for this jump. This is a bug — the
      // version was bumped without a matching migration.
      throw new Error(`dofs schema: no migration registered for v${version} -> v${target}`);
    }
    next.migrator(db);
    version = next.to;
  }
  return version;
}
